import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

interface User {
  id: number;
  username: string;
  email: string;
  email_verified: boolean;
  created_at: string;
  updated_at: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  emailVerificationRequired: boolean;
  login: (username: string, password: string) => Promise<{ success: boolean; message: string }>;
  register: (username: string, email: string, password: string) => Promise<{ success: boolean; message: string; emailVerificationRequired?: boolean }>;
  logout: () => void;
  updateProfile: (data: Partial<User>) => void;
  refreshProfile: () => Promise<void>;
  resendVerificationEmail: () => Promise<{ success: boolean; message: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const API_BASE_URL = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:3001/api`;

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
});

const storedToken = localStorage.getItem('fragrance-organizer-token');
if (storedToken) {
  api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [emailVerificationRequired, setEmailVerificationRequired] = useState(false);
  const [isLoginAttempt, setIsLoginAttempt] = useState(false);

  const isAuthenticated = !!user && !!token;

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    setEmailVerificationRequired(false);
    localStorage.removeItem('fragrance-organizer-token');
    localStorage.removeItem('fragrance-organizer-user');
  
    if (token) {
      api.post('/auth/logout').catch(console.error);
    }
  }, [token]);

  // Set up axios interceptors
  useEffect(() => {
    // Request interceptor to add auth token
    const requestInterceptor = api.interceptors.request.use(
      (config) => {
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor to handle auth errors (but not during login/register)
    const responseInterceptor = api.interceptors.response.use(
      (response) => response,
      (error) => {
        // Don't auto-logout on auth endpoints and don't interfere with forms
        const isAuthEndpoint = error.config?.url?.includes('/auth/');
        const isCheckEndpoint = error.config?.url?.includes('/check-');
        const isLoginAttempt = error.config?.url?.includes('/auth/login');
        const isRegisterAttempt = error.config?.url?.includes('/auth/register');
        
        // Only auto-logout for 401s on protected routes, not auth attempts
        if (error.response?.status === 401 && 
            !isAuthEndpoint && 
            !isCheckEndpoint && 
            !isLoginAttempt &&
            !isRegisterAttempt &&
            isAuthenticated) {
          console.log('🔓 Auto-logout triggered for protected route 401');
          logout();
        } else if (error.response?.status === 401 && (isLoginAttempt || isRegisterAttempt)) {
          console.log('🔒 Ignoring 401 for auth attempt - letting form handle it');
        }
        
        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.request.eject(requestInterceptor);
      api.interceptors.response.eject(responseInterceptor);
    };
  }, [token, isAuthenticated, logout]);

  // Load user data from localStorage on mount
  useEffect(() => {
    const loadStoredAuth = async () => {
      try {
        const storedToken = localStorage.getItem('fragrance-organizer-token');
        const storedUser = localStorage.getItem('fragrance-organizer-user');

        if (storedToken && storedUser) {
          const parsedUser = JSON.parse(storedUser);
          setToken(storedToken);
          setUser(parsedUser);
          
          // Don't verify token on load to prevent auth issues during forms
          // Token will be verified when making API calls
        }
      } catch (error) {
        console.error('Error loading stored auth:', error);
        // Clear invalid stored data
        localStorage.removeItem('fragrance-organizer-token');
        localStorage.removeItem('fragrance-organizer-user');
      } finally {
        setIsLoading(false);
      }
    };

    loadStoredAuth();
  }, []);

  const login = async (username: string, password: string): Promise<{ success: boolean; message: string }> => {
    try {
      console.log('AuthContext: Starting login...'); // Debug log
      setIsLoginAttempt(true);
      
      const response = await api.post('/auth/login', { username, password });
      
      console.log('AuthContext: Login response:', response.data); // Debug log
      
      if (response.data.success) {
        const { user: userData, token: userToken, emailVerificationRequired: emailVerificationReq } = response.data.data;
        
        console.log('AuthContext: Setting user data...'); // Debug log
        setUser(userData);
        setToken(userToken);
        setEmailVerificationRequired(emailVerificationReq || false);
        
        // Store in localStorage
        localStorage.setItem('fragrance-organizer-token', userToken);
        localStorage.setItem('fragrance-organizer-user', JSON.stringify(userData));
        
        console.log('AuthContext: Login successful'); // Debug log
        return { success: true, message: 'Login successful' };
      }
      
      console.log('AuthContext: Login failed - invalid credentials'); // Debug log
      // Don't change any auth state on failure
      return { success: false, message: response.data.message || 'Login failed' };
    } catch (error: any) {
      console.error('AuthContext: Login error:', error);
      let message = 'Login failed. Please try again.';
      
      if (error.response?.status === 401) {
        message = 'Invalid username or password. Please check your credentials.';
      } else if (error.response?.status === 429) {
        message = 'Too many login attempts. Please wait a few minutes and try again.';
      } else if (error.response?.data?.message) {
        message = error.response.data.message;
      }
      
      console.log('AuthContext: Returning error:', message); // Debug log
      // Don't change any auth state on error
      return { success: false, message };
    } finally {
      setIsLoginAttempt(false);
    }
  };

  const register = async (
    username: string,
    email: string,
    password: string
  ): Promise<{ success: boolean; message: string; status?: number; emailVerificationRequired?: boolean }> => {
    try {
      setIsLoading(true);
      const response = await api.post('/auth/register', { username, email, password });
      
      if (response.data.success) {
        const {
          user: userData,
          token: userToken,
          emailVerificationRequired: emailVerificationReq
        } = response.data.data;
        
        setUser(userData);
        setToken(userToken);
        setEmailVerificationRequired(emailVerificationReq || false);
        
        // Store in localStorage
        localStorage.setItem('fragrance-organizer-token', userToken);
        localStorage.setItem('fragrance-organizer-user', JSON.stringify(userData));
        
        return { 
          success: true, 
          message: 'Registration successful', 
          status: 200,
          emailVerificationRequired: emailVerificationRequired || false
        };
      }
      
      return {
        success: false,
        message: response.data.message || 'Registration failed',
        status: response.status,
        emailVerificationRequired: false
      };
    } catch (error: any) {
      console.error('Registration error:', error);
      let message = 'Registration failed. Please try again.';
      let status = error.response?.status;
      
      if (status === 409) {
        message =
          error.response.data?.message ||
          'Username or email already exists. Please try different credentials.';
      } else if (status === 400) {
        message = error.response.data?.message || 'Invalid registration data.';
      } else if (error.response?.data?.message) {
        message = error.response.data.message;
      }
      
      return { success: false, message, status, emailVerificationRequired: false };
    } finally {
      setIsLoading(false);
    }
  };

  const updateProfile = (data: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...data };
      setUser(updatedUser);
      localStorage.setItem('fragrance-organizer-user', JSON.stringify(updatedUser));
    }
  };

  const refreshProfile = async () => {
    try {
      if (!token) return;
      
      const response = await api.get('/auth/profile');
      if (response.data.success) {
        const userData = response.data.data.user;
        setUser(userData);
        localStorage.setItem('fragrance-organizer-user', JSON.stringify(userData));
      }
    } catch (error: any) {
      console.error('Error refreshing profile:', error);
      // If profile refresh fails with 401, token is invalid
      if (error.response?.status === 401) {
        logout();
      }
    }
  };

  const resendVerificationEmail = async () => {
    try {
      if (!token) {
        return { success: false, message: 'Not authenticated' };
      }

      const response = await api.post('/email/resend-verification');
      return {
        success: response.data.success,
        message: response.data.message
      };
    } catch (error: any) {
      console.error('Resend verification error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to resend verification email'
      };
    }
  };

  const value = {
    user,
    token,
    isLoading,
    isAuthenticated,
    emailVerificationRequired,
    login,
    register,
    logout,
    updateProfile,
    refreshProfile,
    resendVerificationEmail,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Export the api instance for use in other components
export { api };
